import {
  MAX_CODE_STEP_SAVE_ERRORS,
  type CodeStepSaveResult,
} from 'ts-components/PiChat/protocol';

export interface LastPersist {
  requestId: string | null;
  ok: boolean;
  status: number | null;
  original: unknown;
}

const MAX_ERROR_DEPTH = 8;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const withoutErrorsShim = (body: Record<string, unknown>) => {
  const { errors, ...rest } = body;
  if (
    isRecord(errors) &&
    Object.keys(rest).every((key) => errors[key] === rest[key])
  ) {
    return errors.errors === undefined
      ? rest
      : { ...rest, errors: errors.errors };
  }
  return body;
};

export const flattenPersistErrors = (
  original: unknown,
  stepId: string
): string[] => {
  const out: string[] = [];
  const walk = (value: unknown, path: string[], depth: number) => {
    if (out.length >= MAX_CODE_STEP_SAVE_ERRORS || depth > MAX_ERROR_DEPTH) {
      return;
    }
    if (typeof value === 'string') {
      out.push(path.length ? `${path.join('.')}: ${value}` : value);
      return;
    }
    if (Array.isArray(value)) {
      const leaves = value.every((item) => typeof item === 'string');
      value.forEach((item, index) =>
        walk(item, leaves ? path : [...path, String(index)], depth + 1)
      );
      return;
    }
    if (isRecord(value)) {
      for (const [key, item] of Object.entries(value)) {
        walk(item, [...path, key], depth + 1);
      }
    }
  };
  if (isRecord(original)) {
    const body = withoutErrorsShim(original);
    if (stepId in body) {
      const { [stepId]: own, ...rest } = body;
      walk(own, [], 0);
      walk(rest, [], 0);
    } else {
      walk(body, [], 0);
    }
  } else {
    walk(original, [], 0);
  }
  return out;
};

const CONFLICT_TEXT = /not up to date/i;

export const mapPersistOutcome = (
  last: LastPersist,
  revision: number | null | undefined,
  stepId: string
): CodeStepSaveResult => {
  if (last.ok) {
    return typeof revision === 'number' ? { ok: true, revision } : { ok: true };
  }
  const { status } = last;
  const errors = flattenPersistErrors(last.original, stepId);
  const withErrors = errors.length ? { errors } : {};
  if (status === 403) {
    return {
      ok: false,
      code: 'forbidden',
      message: 'Not allowed to save this workflow',
    };
  }
  if (status === 409 || errors.some((error) => CONFLICT_TEXT.test(error))) {
    return {
      ok: false,
      code: 'conflict',
      message: 'The workflow changed since it was loaded',
      ...withErrors,
    };
  }
  if (status === 400) {
    return {
      ok: false,
      code: 'rejected',
      message: 'The workflow rejected the code step',
      ...withErrors,
    };
  }
  return {
    ok: false,
    code: 'failed',
    message:
      status != null
        ? `Saving the workflow failed (HTTP ${status})`
        : 'Saving the workflow failed',
  };
};
